import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Boxes, LayoutGrid } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { SubnavPanel, subnavItemClass } from '@/components/shared/section-subnav'
import { ChannelIcon, isChannelKey, useChannelStats, type ChannelKey } from './channel-catalog'
import { parseOAuthResult } from '@/lib/oauth-result'
import { QuotaExceededDialog } from '@/components/billing/quota-exceeded-dialog'

/**
 * Xử lý kết quả redirect OAuth (backend trả về `/integrations?...` khi không bắt đầu từ trang Bắt đầu),
 * báo toast rồi mở thẳng trang của kênh vừa kết nối. Trả về kênh cần mở, hoặc null nếu query string
 * không có kết quả OAuth nào. Thông báo "đã đạt hạn mức" (`quotaExceeded`) hiện popup điều hướng mua
 * thêm/nâng cấp gói thay vì toast — `openQuota` xử lý riêng, không lặp qua `toast[n.type]`.
 */
function consumeOauthResult(sp: URLSearchParams, openQuota: () => void): ChannelKey | null {
  const r = parseOAuthResult(sp)
  if (!r) return null
  for (const n of r.notices) {
    if (n.quotaExceeded) openQuota()
    else toast[n.type](n.text)
  }
  return r.channel
}

/**
 * Khung trang Tích hợp theo bố cục SaleSmartly: menu phụ bên trái ("Tất cả kênh"
 * + nhóm "Kênh đã thêm" kèm số tài khoản), nội dung bên phải.
 * Route: /integrations và /integrations/:channel
 */
export function IntegrationsLayout() {
  const { t } = useTranslation('chanCatalog')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { stats, channels } = useChannelStats()
  const [quotaOpen, setQuotaOpen] = useState(false)

  useEffect(() => {
    const oauthChannel = consumeOauthResult(searchParams, () => setQuotaOpen(true))
    if (oauthChannel) {
      // Dọn query string để refresh không hiện lại toast.
      navigate(`/integrations/${oauthChannel}`, { replace: true })
      return
    }
    // Đường cũ `/integrations?tab=…` (nút "Kết nối lại", dấu trang) → trang kênh.
    const tab = searchParams.get('tab')
    if (isChannelKey(tab)) navigate(`/integrations/${tab}`, { replace: true })
  }, [searchParams, navigate])

  const currentKey = location.pathname.split('/')[2]
  const added = channels.filter((c) => stats[c.key]?.total > 0 || c.key === currentKey)

  return (
    <div className="flex h-full min-h-0">
      <SubnavPanel title={t('layout.title')}>

        <NavLink
          to="/integrations"
          end
          className={({ isActive }) => subnavItemClass(isActive)}
        >
          <LayoutGrid className="h-4 w-4 shrink-0" />
          <span className="truncate">{t('layout.allChannels')}</span>
        </NavLink>
        <NavLink to="/integrations/apps" className={({ isActive }) => subnavItemClass(isActive)}>
          <Boxes className="h-4 w-4 shrink-0" />
          <span className="truncate">{t('layout.apps')}</span>
        </NavLink>

        {added.length > 0 && (
          <>
            <p className="mb-1 mt-4 px-2 text-xs text-muted-foreground">{t('layout.addedChannels')}</p>
            <div className="space-y-0.5">
              {added.map((c) => (
                <NavLink
                  key={c.key}
                  to={`/integrations/${c.key}`}
                  className={({ isActive }) => subnavItemClass(isActive)}
                >
                  <ChannelIcon def={c} size="sm" />
                  <span className="min-w-0 flex-1 truncate">{c.name}</span>
                  {c.kind === 'personal' && (
                    <span className="rounded bg-warning/10 px-1 text-[10px] leading-4 text-warning">Cloud</span>
                  )}
                  <span className="min-w-[22px] rounded-full bg-success/10 px-1.5 text-center text-xs leading-5 text-success tabular-nums">
                    {stats[c.key]?.total ?? 0}
                  </span>
                </NavLink>
              ))}
            </div>
          </>
        )}
      </SubnavPanel>

      <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
        <div className="mx-auto max-w-[1400px] px-6 pb-6">
          <Outlet />
        </div>
      </div>

      <QuotaExceededDialog quotaKey="officialChannels" open={quotaOpen} onOpenChange={setQuotaOpen} />
    </div>
  )
}
